import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../../../components/common/FeatherIcon';
import MarkdownText from '../../../components/common/MarkdownText';
import { useAxonPageContext } from '../../../context/AxonPageContext';
import { loadChatHistory, persistChatHistory } from '../../../services/ai-explorer/chatHistoryStore';
import { readAgentMode, writeAgentMode } from '../../../services/ai-explorer/chatStorage';
import { requestHelperAnswer } from '../../../services/ai-explorer/helperService';
import { isFeatureEnabled } from '../../../utils/featureFlags';
import { conversationTitle, newId } from './agentModel';
import Composer from './Composer';
import HistoryPane from './HistoryPane';
import MessageItem from './MessageItem';
import { HELPER_QUICK_ACTIONS, buildContextCards, buildEnrichedContext, quickActionsFor, questionForAction } from './panelContext';
import ShortcutsDialog from './ShortcutsDialog';
import useAgentConversation from './useAgentConversation';
import useCanvas from './useCanvas';
import useVoiceInput from './useVoiceInput';
import './agent.css';
import './AgentPanel.css';

/**
 * Open the agent from anywhere: `openAgent({ question, subject })` sends the question;
 * `openAgent({ question, draft: true })` puts it in the composer, focused and unsent, to edit first.
 */
export const OPEN_AGENT_EVENT = 'saf3ai:open-agent';
export const openAgent = (detail = {}) => window.dispatchEvent(new CustomEvent(OPEN_AGENT_EVENT, { detail }));

const MIN_W = 320;

/**
 * The Saf3AI Agent as a panel over the current page. Same conversation and
 * history as the /ai-explorer page; it adds what only makes sense in place:
 * the page greeting with its headline numbers, quick actions for the page,
 * a "you're now on X" notice when navigating with it open, Helper mode, and
 * Expand to continue the thread full-width.
 */
export default function AgentPanel({ isOpen, onClose, initialMessage, initialSubject, initialDraft = false, launchKey }) {
  const { t } = useTranslation('aiExplorer');
  const navigate = useNavigate();
  const location = useLocation();
  const { pageContext } = useAxonPageContext();
  const pageId = pageContext?.page?.id || 'unknown';
  const pageTitle = pageContext?.page?.title || '';
  const pageContextRef = useRef(pageContext); pageContextRef.current = pageContext;

  const [mode, setMode] = useState(() => readAgentMode());
  const [histOpen, setHistOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [resizing, setResizing] = useState(false);
  const panelRef = useRef(null);

  const c = useAgentConversation({
    t,
    enabled: isOpen,
    getPageContext: () => buildEnrichedContext(pageContextRef.current, pageContextRef.current?.page?.id),
  });
  const {
    messages, conversationId, title, bookmarked, busy, input, setInput, windowId, changeWindow, toolPrefs, changeToolPrefs,
    toasts, toast, shortcutsOpen, setShortcutsOpen, history, historyQuery, setHistoryQuery, loadHistory, latest, setLatest,
    dashboards, actions, ask, stop, send, newChat, editLast, exportTranscript, togglePin, runCommand,
    openConversation, deleteConversation, ensureSaved, addNotice, patchLastNotice, textareaRef, searchRef, bodyRef, scrollToEnd,
  } = c;

  // Pins from the panel, on any page, go to the same active canvas as the agent page.
  const canvasOn = isFeatureEnabled('AGENT_CANVAS');
  const cv = useCanvas({ t, toast, ensureSaved, enabled: isOpen && canvasOn });
  const canvasActions = canvasOn ? {
    pinned: (m) => cv.isPinned(m.id),
    pin: (m) => cv.pin(m, { question: c.questionFor(m), context: c.contextFor(m), pageContext: c.pageContextNow() }),
    unpin: cv.unpin,
  } : undefined;

  // ── greeting, context switches, initial question ────────────────
  const greeting = useCallback((cards, { back = false, moved = false } = {}) => {
    if (!pageTitle || pageId === 'unknown') return t('agent.greetingGeneric');
    const lead = moved ? t('agent.nowOn', { title: pageTitle }) : back ? t('agent.welcomeBackOn', { title: pageTitle }) : t('agent.youAreOn', { title: pageTitle });
    return `${lead} ${cards.length ? t('agent.greetingCards') : t('agent.greetingAsk')}`;
  }, [pageId, pageTitle, t]);

  const openedRef = useRef(false);
  const prevPageRef = useRef(pageId);
  const sentInitialRef = useRef(null);
  useEffect(() => {
    if (!isOpen) { openedRef.current = false; return; }
    if (openedRef.current) return;
    openedRef.current = true;
    prevPageRef.current = pageId;
    if (mode !== 'analyst' || messages.length > 0) return;
    const cards = buildContextCards(pageContext, pageId);
    // Resume the latest thread so the panel and the page carry one history.
    if (latest?.id && !initialMessage) {
      openConversation(latest).then((ok) => addNotice(greeting(cards, { back: ok }), cards));
      setLatest(null);
    } else {
      addNotice(greeting(cards), cards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== 'analyst' || prevPageRef.current === pageId) return;
    prevPageRef.current = pageId;
    if (pageId === 'unknown') return;
    const cards = buildContextCards(pageContext, pageId);
    addNotice(greeting(cards, { moved: true }), cards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pageId]);

  // The page's summary often lands after the panel opened: fill the cards in.
  const summaryKey = JSON.stringify(pageContext?.summary || {});
  useEffect(() => {
    if (!isOpen || mode !== 'analyst') return;
    const cards = buildContextCards(pageContext, pageId);
    if (cards.length) patchLastNotice(greeting(cards), cards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey, isOpen]);

  useEffect(() => {
    if (!isOpen || !initialMessage) return;
    const key = `${launchKey || ''}|${initialMessage}|${JSON.stringify(initialSubject || null)}`;
    if (sentInitialRef.current === key) return;
    sentInitialRef.current = key;
    if (mode !== 'analyst') { writeAgentMode('analyst'); setMode('analyst'); }
    const timer = setTimeout(() => {
      if (initialDraft) {
        // A suggested prompt: in the composer for the person to edit and send.
        setInput(initialMessage);
        textareaRef.current?.focus();
      } else {
        ask(initialMessage, { subject: initialSubject || undefined });
      }
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMessage, initialSubject, initialDraft, launchKey]);

  // ── helper mode (conceptual Q&A, its own small store) ───────────
  const [helperMsgs, setHelperMsgs] = useState([]);
  const [helperBusy, setHelperBusy] = useState(false);
  const helperIdRef = useRef(null);
  const helperLoadedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || mode !== 'helper' || helperLoadedRef.current) return;
    helperLoadedRef.current = true;
    loadChatHistory('helper').then(({ conversationId: id, messages: restored }) => {
      helperIdRef.current = id;
      if (restored.length) setHelperMsgs(restored.map((m) => ({ ...m, id: newId() })));
    });
  }, [isOpen, mode]);
  const askHelper = async (question) => {
    const q = String(question || '').trim();
    if (!q || helperBusy) return;
    const pending = { id: newId(), role: 'agent', content: '', loading: true };
    const next = [...helperMsgs, { id: newId(), role: 'user', content: q }, pending];
    setHelperMsgs(next); setInput(''); setHelperBusy(true); scrollToEnd();
    let final;
    try {
      const res = await requestHelperAnswer(q);
      final = next.map((m) => (m.id === pending.id ? { ...m, content: res.response, loading: false, routeToAnalyst: res.routeToAnalyst, handoffQuery: q } : m));
    } catch (err) {
      final = next.map((m) => (m.id === pending.id ? { ...m, content: err?.message || t('agent.errorGeneric'), loading: false, error: true } : m));
    }
    setHelperMsgs(final); setHelperBusy(false); scrollToEnd();
    helperIdRef.current = await persistChatHistory({ conversationId: helperIdRef.current, agentMode: 'helper', messages: final });
  };
  const switchMode = (next) => {
    if (next === mode) return;
    writeAgentMode(next); setMode(next); setHistOpen(false);
    if (next === 'analyst' && messages.length === 0) { const cards = buildContextCards(pageContext, pageId); addNotice(greeting(cards), cards); }
  };
  const handoff = (q) => { switchMode('analyst'); setInput(q || ''); setTimeout(() => textareaRef.current?.focus(), 0); };

  // ── size, body push, keys ───────────────────────────────────────
  useEffect(() => {
    if (!isOpen) { setPanelWidth(null); setFullscreen(false); setHistOpen(false); return undefined; }
    document.body.classList.add('axon-panel-open');
    document.documentElement.style.setProperty('--axon-panel-width', fullscreen ? '100vw' : (panelWidth ? `${panelWidth}px` : '40vw'));
    return () => { document.body.classList.remove('axon-panel-open'); document.documentElement.style.removeProperty('--axon-panel-width'); };
  }, [isOpen, panelWidth, fullscreen]);

  const onGrip = useCallback((e) => {
    e.preventDefault();
    setFullscreen(false); setResizing(true);
    const startX = e.clientX;
    const startW = panelRef.current ? panelRef.current.getBoundingClientRect().width : (panelWidth || window.innerWidth * 0.4);
    document.body.style.cursor = 'ew-resize'; document.body.style.userSelect = 'none';
    const move = (ev) => setPanelWidth(Math.min(Math.max(startW + (startX - ev.clientX), MIN_W), window.innerWidth * 0.95));
    const up = () => {
      document.body.style.cursor = ''; document.body.style.userSelect = ''; setResizing(false);
      document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
  }, [panelWidth]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === 'Escape') {
        if (shortcutsOpen) setShortcutsOpen(false);
        else if (busy) stop();
        else if (histOpen) setHistOpen(false);
        else onClose?.();
      } else if (mod && e.key.toLowerCase() === 'k') { e.preventDefault(); setHistOpen(true); setTimeout(() => searchRef.current?.focus(), 0); }
      else if (mod && e.shiftKey && e.key.toLowerCase() === 'o') { e.preventDefault(); newChat(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, histOpen, isOpen, newChat, onClose, searchRef, setShortcutsOpen, shortcutsOpen, stop]);

  const voice = useVoiceInput({
    onTranscript: (text) => { setInput(text); textareaRef.current?.focus(); },
    onError: (key) => toast(t(key), 'error'),
  });

  const expand = async () => {
    const id = await ensureSaved();
    onClose?.();
    const search = canvasOn && cv.canvas?.id ? `?canvas=${encodeURIComponent(cv.canvas.id)}` : '';
    navigate(`${id ? `/ai-explorer/c/${id}` : '/ai-explorer'}${search}`);
  };

  if (!isOpen) return null;

  const analyst = mode === 'analyst';
  const hasUserTurn = messages.some((m) => m.role === 'user');
  const quick = analyst ? (hasUserTurn ? [] : quickActionsFor(pageContext, pageId)) : HELPER_QUICK_ACTIONS;
  const lastAgentId = [...messages].reverse().find((m) => m.role === 'agent' && !m.ephemeral)?.id;
  const onPage = location.pathname.startsWith('/ai-explorer');

  const panel = (
    <div
      ref={panelRef}
      className={`sa3-agent sa3-panel show${fullscreen ? ' fullscreen' : ''}${resizing ? ' resizing' : ''}`}
      style={fullscreen ? { width: '100vw', maxWidth: 'none' } : (panelWidth ? { width: panelWidth, maxWidth: 'none' } : undefined)}
      role="complementary" aria-label={t('agent.title')}
    >
      {!fullscreen && <div className="sa3-panel-grip" onMouseDown={onGrip} title={t('agent.dragToResize')} />}
      <section className="sa3-main">
        <header className="sa3-top">
          <div className="sa3-top-l">
            <span className="sa3-mark" aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 7 }}><FeatherIcon name="zap" size={14} /></span>
            <h1>{analyst && hasUserTurn ? (title || conversationTitle(messages, t('agent.untitled'))) : t('agent.title')}</h1>
            <div className="sa3-modes" role="tablist" aria-label={t('agent.mode')}>
              <button type="button" role="tab" aria-selected={analyst} className={analyst ? 'on' : ''} onClick={() => switchMode('analyst')} title={t('agent.analystHint')}><FeatherIcon name="bar-chart-2" size={13} />{t('agent.analyst')}</button>
              <button type="button" role="tab" aria-selected={!analyst} className={!analyst ? 'on' : ''} onClick={() => switchMode('helper')} title={t('agent.helperHint')}><FeatherIcon name="help-circle" size={13} />{t('agent.helper')}</button>
            </div>
          </div>
          <div className="sa3-top-r">
            {analyst && (
              <>
                <button type="button" className={`sa3-ibtn${histOpen ? ' on' : ''}`} onClick={() => setHistOpen((o) => !o)} aria-label={t('agent.conversations')} title={t('agent.conversations')} aria-pressed={histOpen}><FeatherIcon name="clock" size={16} /></button>
                {hasUserTurn && <button type="button" className="sa3-ibtn" onClick={exportTranscript} aria-label={t('agent.downloadTranscript')} title={t('agent.downloadTranscript')}><FeatherIcon name="download" size={16} /></button>}
                {hasUserTurn && <button type="button" className={`sa3-ibtn${bookmarked ? ' on' : ''}`} onClick={() => togglePin(null)} disabled={!conversationId} aria-label={bookmarked ? t('agent.unpin') : t('agent.pin')} title={bookmarked ? t('agent.unpin') : t('agent.pin')}><FeatherIcon name="bookmark" size={16} /></button>}
                <button type="button" className="sa3-ibtn" onClick={newChat} aria-label={t('agent.newChat')} title={t('agent.newChat')}><FeatherIcon name="plus" size={18} /></button>
                {!onPage && <button type="button" className="sa3-ibtn" onClick={expand} aria-label={t('agent.expand')} title={t('agent.expand')}><FeatherIcon name="arrow-up-right" size={16} /></button>}
              </>
            )}
            <button type="button" className="sa3-ibtn" onClick={() => setFullscreen((f) => !f)} aria-label={fullscreen ? t('agent.exitFullscreen') : t('agent.fullscreen')} title={fullscreen ? t('agent.exitFullscreen') : t('agent.fullscreen')}><FeatherIcon name={fullscreen ? 'minimize-2' : 'maximize-2'} size={16} /></button>
            <button type="button" className="sa3-ibtn" onClick={onClose} aria-label={t('agent.close')} title={t('agent.close')}><FeatherIcon name="x" size={18} /></button>
          </div>
        </header>

        {analyst && (
          <HistoryPane
            open={histOpen} items={history.items} total={history.total} loading={history.loading} error={history.error}
            onLoadMore={() => loadHistory(history.page + 1)} onRetry={() => loadHistory(1)}
            activeId={conversationId} onOpen={(item) => openConversation(item).then(() => setHistOpen(false))} onNew={() => { newChat(); setHistOpen(false); }}
            onDelete={deleteConversation} onTogglePin={togglePin}
            query={historyQuery} onQuery={setHistoryQuery} searchRef={searchRef} onClose={() => setHistOpen(false)}
          />
        )}

        <div className="sa3-body" ref={bodyRef}>
          <div className="sa3-col">
            <div className="sa3-thread">
              {analyst ? messages.map((m) => (
                <MessageItem key={m.id} m={m} isLastAgent={m.id === lastAgentId} busy={busy} dashboards={dashboards} actions={actions} canvas={canvasActions} />
              )) : (
                <>
                  {helperMsgs.length === 0 && (
                    <div className="sa3-a sa3-notice"><span className="sa3-mark" aria-hidden="true"><FeatherIcon name="help-circle" size={15} /></span><div className="sa3-ab"><MarkdownText>{t('agent.helperIntro')}</MarkdownText></div></div>
                  )}
                  {helperMsgs.map((m) => (m.role === 'user' ? <div key={m.id} className="sa3-u">{m.content}</div> : (
                    <div key={m.id} className="sa3-a">
                      <span className="sa3-mark" aria-hidden="true"><FeatherIcon name="help-circle" size={15} /></span>
                      <div className="sa3-ab">
                        {m.loading ? <div className="sa3-thinking"><i /><i /><i /><span>{t('agent.thinking')}</span></div> : <MarkdownText>{m.content}</MarkdownText>}
                        {m.routeToAnalyst && <div><button type="button" className="sa3-handoff" onClick={() => handoff(m.handoffQuery)}><FeatherIcon name="bar-chart-2" size={14} />{t('agent.continueInAnalyst')}</button></div>}
                      </div>
                    </div>
                  )))}
                </>
              )}
            </div>
          </div>
        </div>

        {quick.length > 0 && (
          <div className="sa3-quick" aria-label={t('agent.quickActions')}>
            {quick.map((a) => (
              <button key={a.label} type="button" disabled={analyst ? busy : helperBusy} title={a.description || undefined}
                onClick={() => (analyst ? ask(questionForAction(a.label)) : askHelper(a.label))}>
                <FeatherIcon name={a.icon || 'zap'} size={13} />{a.label}
              </button>
            ))}
          </div>
        )}

        <div className="sa3-dock">
          <Composer
            value={input} onChange={setInput}
            onSend={analyst ? send : () => askHelper(input)} onStop={stop}
            onCommand={(cmd) => runCommand({ command: cmd, arg: '' })} onEditLast={editLast}
            busy={analyst ? busy : helperBusy} textareaRef={textareaRef}
            placeholder={analyst ? (hasUserTurn ? t('agent.followUpPlaceholder') : t('agent.panelPlaceholder', { title: pageTitle || t('agent.thisPage') })) : t('agent.helperPlaceholder')}
            windowId={windowId} onWindow={changeWindow} voice={voice} toolPrefs={toolPrefs} onToolPrefs={changeToolPrefs} hideWindow={!analyst}
          />
        </div>
      </section>

      <div className="sa3-toasts" aria-live="polite">
        {toasts.map((x) => (
          <div key={x.id} className={`sa3-toast ${x.kind}`}>
            {x.text}
            {x.action && <button type="button" className="sa3-toast-act" onClick={() => { x.action.onClick(); c.dismissToast(x.id); }}>{x.action.label}</button>}
          </div>
        ))}
      </div>
      {shortcutsOpen && <ShortcutsDialog inPanel onClose={() => setShortcutsOpen(false)} />}
    </div>
  );

  return createPortal(panel, document.body);
}
